import type { BillingPurchaseView } from "@server/types/responseTypes";
/** Authoritative lifecycle fields may arrive from Checkout before the full read supplies display details. */
export type BillingPurchaseSnapshot = Pick<
  BillingPurchaseView,
  "purchase_id" | "payment_status" | "fulfillment_status" | "review_required"
> &
  Partial<BillingPurchaseView>;

/** Recovery identity only: no financial authority or provider secrets are stored in the browser. */
export type BillingAttempt = {
  formKey: string;
  offerId: string;
  purchaseId?: string;
};
const key = (orgId: string) => `billing-attempt:${orgId}`;
const purchasePattern = /^purchase_[a-f0-9]{64}$/;

/** Loads a persisted retry identity; malformed data is ignored, storage failures surface to callers. */
export function readAttempt(orgId: string): BillingAttempt | null {
  const raw = sessionStorage.getItem(key(orgId));
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    if (
      typeof data.formKey !== "string" ||
      !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(
        data.formKey,
      ) ||
      typeof data.offerId !== "string" ||
      !data.offerId ||
      (data.purchaseId && !purchasePattern.test(data.purchaseId))
    )
      return null;
    return data;
  } catch {
    return null;
  }
}

/** Persists in this tab before creating Checkout; failure must prevent creating an unrecoverable purchase. */
export function saveAttempt(orgId: string, attempt: BillingAttempt): void {
  sessionStorage.setItem(key(orgId), JSON.stringify(attempt));
}

/** Removes only the matching authoritative terminal attempt, protecting newer attempts. */
export function clearAttempt(orgId: string, purchaseId: string): void {
  if (readAttempt(orgId)?.purchaseId === purchaseId)
    sessionStorage.removeItem(key(orgId));
}

/** Validated URL identity; outcome is display context only and never authorizes credit. */
export type BillingReturn = { organizationId: string; purchaseId: string };

/** Rejects partial, repeated and malformed returns; server authorization still owns purchase identity. */
export function parseBillingReturn(search: Record<string, unknown>): {
  returnTo?: BillingReturn;
  invalid: boolean;
} {
  const { organization_id: org, purchase_id: purchase, checkout } = search;
  if (org === undefined && purchase === undefined && checkout === undefined)
    return { invalid: false };
  if (
    typeof org !== "string" ||
    !/^[a-zA-Z0-9_-]{1,128}$/.test(org) ||
    typeof purchase !== "string" ||
    !purchasePattern.test(purchase) ||
    (checkout !== "success" && checkout !== "cancel")
  )
    return { invalid: true };
  return {
    returnTo: { organizationId: org, purchaseId: purchase },
    invalid: false,
  };
}

/** Review is independent of credit fulfillment; retain the legacy status guard during staggered rollout. */
export function requiresPurchaseReview(
  purchase: Pick<BillingPurchaseView, "fulfillment_status" | "review_required">,
): boolean {
  return (
    purchase.review_required === true ||
    purchase.fulfillment_status === "manual_review"
  );
}

/** Clears a matching terminal attempt from either a status read or create replay; review always retains its recovery reference. */
export function reconcileAttempt(
  orgId: string,
  purchase: Pick<
    BillingPurchaseView,
    "purchase_id" | "payment_status" | "fulfillment_status" | "review_required"
  >,
): void {
  if (
    !requiresPurchaseReview(purchase) &&
    (purchase.fulfillment_status === "fulfilled" ||
      purchase.payment_status === "expired" ||
      purchase.payment_status === "failed")
  ) {
    clearAttempt(orgId, purchase.purchase_id);
  }
}
